// ActionModal — wraps step-up + reason form + action fire into a single flow.
// Spec: docs/plans/admin-tooling.md D-62, v1-A mutating actions

import { useState } from 'react';
import { apiFetch } from '@/lib/api';
import { StepUpModal } from './step-up-modal';
import { getIntentLabel } from '@/lib/admin/intent-labels';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ActionDef = {
  intent: string;
  label: string;
  userId: number;
  userLastIp: string | null;
};

interface ActionModalProps {
  action: ActionDef;
  onDone: () => void;
  onCancel: () => void;
}

type Phase = 'stepup' | 'confirm' | 'submitting' | 'success' | 'error';

// ── Intent → API path mapping ──────────────────────────────────────────────────

function resolveApiPath(intent: string, userId: number, userLastIp: string | null): string {
  switch (intent) {
    case 'account-lock':    return `/admin/users/${userId}/lock`;
    case 'account-unlock':  return `/admin/users/${userId}/unlock`;
    case 'fedjail':         return `/admin/users/${userId}/fedjail`;
    case 'fedjail-clear':   return `/admin/users/${userId}/fedjail/clear`;
    case 'force-logout':    return `/admin/users/${userId}/force-logout`;
    case 'password-reset':  return `/admin/users/${userId}/password-reset`;
    case 'disable-2fa':     return `/admin/users/${userId}/disable-2fa`;
    case 'recover-email':   return `/admin/users/${userId}/recover-email`;
    case 'ip-ban':          return `/admin/ip-ban`;
    case 'ip-unban':        return `/admin/ip-ban/${userLastIp ?? ''}/clear`;
    default:                return `/admin/users/${userId}/action/${intent}`;
  }
}

function buildBody(
  intent: string,
  reason: string,
  durationDays: string,
  passwordResetMode: string,
  recoverNewEmail: string,
  userLastIp: string | null,
): Record<string, unknown> {
  const base: Record<string, unknown> = { reason };

  if (intent === 'fedjail') {
    base['duration_days'] = Number(durationDays);
  }
  if (intent === 'password-reset') {
    base['mode'] = passwordResetMode;
  }
  if (intent === 'recover-email') {
    base['new_email'] = recoverNewEmail;
  }
  if (intent === 'ip-ban') {
    base['ip'] = userLastIp;
  }

  return base;
}

// ── ModalShell ────────────────────────────────────────────────────────────────

function ModalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
      <div className="relative w-full max-w-sm mx-4 rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] space-y-4">
        {children}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ActionModal({ action, onDone, onCancel }: ActionModalProps) {
  const [phase, setPhase] = useState<Phase>('stepup');
  const [stepupToken, setStepupToken] = useState('');
  const [reason, setReason] = useState('');
  const [durationDays, setDurationDays] = useState('1');
  const [passwordResetMode, setPasswordResetMode] = useState('send-email');
  const [recoverNewEmail, setRecoverNewEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleVerified = (token: string) => {
    setStepupToken(token);
    setPhase('confirm');
  };

  const handleConfirm = () => {
    if (reason.trim().length < 3) return;
    if (action.intent === 'recover-email' && recoverNewEmail.trim().length === 0) return;

    setPhase('submitting');
    const path = resolveApiPath(action.intent, action.userId, action.userLastIp);
    const body = buildBody(action.intent, reason.trim(), durationDays, passwordResetMode, recoverNewEmail.trim(), action.userLastIp);

    void (async () => {
      const res = await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'X-Stepup-Token': stepupToken },
      });

      if (!res.ok) {
        const data = res.data as Record<string, unknown>;
        setErrorMsg((data['error'] as string | undefined) ?? 'Action failed. Please try again.');
        setPhase('error');
        return;
      }

      setPhase('success');
      setTimeout(() => { onDone(); }, 800);
    })();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Step-up phase delegates to StepUpModal
  if (phase === 'stepup') {
    return (
      <StepUpModal
        actionIntent={action.intent}
        targetUserId={action.userId}
        onVerified={handleVerified}
        onCancel={onCancel}
      />
    );
  }

  const intentLabel = getIntentLabel(action.intent);

  return (
    <ModalShell>
      <h2 className="text-base font-semibold text-ink-high">{intentLabel}</h2>

      {/* Confirm phase */}
      {(phase === 'confirm' || phase === 'submitting') && (
        <div className="space-y-4">
          <p className="text-sm text-ink-mid">
            You are about to perform: <strong className="text-ink-high">{intentLabel}</strong>
          </p>

          {/* Fedjail: sentence length.
              #793 — this used to be labelled "Duration (days)", which read as a
              temporary ban. It is not one. The daily cron decrements
              `fedjail.days` only `WHERE days > 1` and clears `users.fed_jail`
              only `WHERE days = 0` (cron/daily-fed-update.ts:59,61-63), so the
              counter floors at 1 and the ban NEVER lifts on its own. That
              non-expiry is legacy-faithful (awake-cron-main/fedupdate.php:4,
              project Q-8) — the copy is what was wrong, not the cron.
              `max` is 365 to match the server, which rejects anything higher
              with `duration_days_out_of_range`
              (packages/engine/src/admin/action-validators.ts:88). */}
          {action.intent === 'fedjail' && (
            <div className="space-y-1">
              <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="fedjail-duration">
                Sentence length (days, 1–365)
              </label>
              <input
                id="fedjail-duration"
                type="number"
                min={1}
                max={365}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary"
                disabled={phase === 'submitting'}
                aria-describedby="fedjail-duration-help"
              />
              <p id="fedjail-duration-help" className="text-xs text-ink-low">
                This sentence does <strong className="text-ink-mid">not</strong> expire on its
                own. The counter stops at 1 day and the account stays locked out of the entire
                game until a staff member runs Clear Fed Jail. Treat this number as a record of
                the sentence issued, not a timer.
              </p>
            </div>
          )}

          {/* Password reset: mode radio */}
          {action.intent === 'password-reset' && (
            <fieldset className="space-y-1">
              <legend className="text-xs font-medium uppercase tracking-wider text-ink-mid">Mode</legend>
              <label className="flex items-center gap-2 text-sm text-ink-mid">
                <input
                  type="radio"
                  name="password-reset-mode"
                  value="send-email"
                  checked={passwordResetMode === 'send-email'}
                  onChange={() => setPasswordResetMode('send-email')}
                  disabled={phase === 'submitting'}
                />
                Send email
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-mid">
                <input
                  type="radio"
                  name="password-reset-mode"
                  value="invalidate-only"
                  checked={passwordResetMode === 'invalidate-only'}
                  onChange={() => setPasswordResetMode('invalidate-only')}
                  disabled={phase === 'submitting'}
                />
                Invalidate only
              </label>
            </fieldset>
          )}

          {/* Account recovery: new email input */}
          {action.intent === 'recover-email' && (
            <div className="space-y-1">
              <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="recover-new-email">
                New Email Address <span className="text-destructive-strong">*</span>
              </label>
              <input
                id="recover-new-email"
                type="email"
                value={recoverNewEmail}
                onChange={(e) => setRecoverNewEmail(e.target.value)}
                className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="player@newdomain.com"
                disabled={phase === 'submitting'}
                autoComplete="off"
              />
            </div>
          )}

          {/* Reason textarea */}
          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="action-reason">
              Reason
            </label>
            <textarea
              id="action-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Required (min 3 chars)"
              disabled={phase === 'submitting'}
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                phase === 'submitting' ||
                reason.trim().length < 3 ||
                (action.intent === 'recover-email' && recoverNewEmail.trim().length === 0)
              }
              className="flex-1 rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-strong disabled:opacity-50"
            >
              {phase === 'submitting' ? 'Submitting…' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={phase === 'submitting'}
              className="text-sm text-ink-low hover:text-ink-mid underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Success */}
      {phase === 'success' && (
        <p className="text-sm text-[--color-status-ok] text-center py-4">Done.</p>
      )}

      {/* Error */}
      {phase === 'error' && (
        <div className="space-y-3">
          <p role="alert" className="text-sm text-destructive-strong">{errorMsg}</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { setPhase('confirm'); }}
              className="flex-1 rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-strong"
            >
              Back
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-ink-low hover:text-ink-mid underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
